//@ts-nocheck
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation, Routes, Route, Navigate } from 'react-router-dom'
import { Auth0Provider, useAuth0, AppState } from '@auth0/auth0-react'
import { Box, Button, Stack, Typography, Alert, CircularProgress } from '@mui/material'
import { clearAuthPath } from '../LoginChooser'
import { useGlobalStateContext } from '../../context/globalState'
import { useMyAccess, isAdminAccess, clearMyAccessCache } from '../../hooks/useMyAccess'
import { logUserAction } from '../../utils/apiUtils'
import { CONSTANTS } from '../../CONSTANTS'
import ProfilePage from '../ProfilePage'
import AdminLandingPage from '../AdminLandingPage'
import Settings from '../Settings'
import SignOutForm from '../../components/SignOutForm'
import PassportQualificationForm from '../../components/PassportQualificationForm'
import TermsAndCondition from '../../components/TermsAndConditions'
import { clearAllDrafts } from '../../components/BulkOnboardingGrid/draftStorage'

const {
  REACT_APP_AUTH0_DOMAIN: AUTH0_DOMAIN = '',
  REACT_APP_AUTH0_AUDIENCE: AUTH0_AUDIENCE = '',
  REACT_APP_AUTH0_REDIRECT_URI: AUTH0_REDIRECT_URI = window.location.origin,
  // Two Auth0 SPA apps in the same tenant. Passwordless and database connections
  // cannot coexist on one app, so email/SMS live on the passwordless app and
  // username/password on the password app. Same domain + audience, so the backend
  // authorizer (which checks issuer/audience/sub, never client_id) is unaffected.
  REACT_APP_AUTH0_PASSWORDLESS_CLIENT_ID: PASSWORDLESS_CLIENT_ID = '',
  REACT_APP_AUTH0_PASSWORD_CLIENT_ID: PASSWORD_CLIENT_ID = '',
} = process.env

// Login methods shown on the sign-in step. Each method maps to WHICH app
// (client id) and WHICH connection. A method appears only when both are
// configured, so enabling SMS later is just setting its connection - the button
// then shows for everyone (it lives on the passwordless app next to email).
const LOGIN_METHODS: {
  key: string
  label: string
  clientId: string
  connection: string
}[] = [
  {
    key: 'email',
    label: 'Email (one-time code)',
    clientId: PASSWORDLESS_CLIENT_ID,
    connection: process.env.REACT_APP_AUTH0_EMAIL_CONNECTION || 'email',
  },
  {
    key: 'password',
    label: 'Username & password',
    clientId: PASSWORD_CLIENT_ID,
    connection: process.env.REACT_APP_AUTH0_DB_CONNECTION || '',
  },
  {
    key: 'sms',
    label: 'Mobile (SMS code)',
    clientId: PASSWORDLESS_CLIENT_ID,
    connection: process.env.REACT_APP_AUTH0_SMS_CONNECTION || '',
  },
].filter((method) => method.clientId && method.connection)

// The chosen app (client id) + connection persist in localStorage so they survive
// the Auth0 redirect and page reloads (the SDK session is also keyed by client id).
// The pending flag is per-tab (sessionStorage) and drives the one-shot auto-login.
const CLIENT_ID_KEY = 'subconAuthClientId'
const CONNECTION_KEY = 'subconAuthConnection'
const PENDING_KEY = 'subconLoginPending'

const persistSelection = (clientId: string, connection: string): void => {
  try {
    localStorage.setItem(CLIENT_ID_KEY, clientId)
    localStorage.setItem(CONNECTION_KEY, connection)
    sessionStorage.setItem(PENDING_KEY, '1')
  } catch {
    /* storage unavailable - ignore */
  }
}
const readSelectedClientId = (): string => {
  try {
    return localStorage.getItem(CLIENT_ID_KEY) || ''
  } catch {
    return ''
  }
}
const readAuthConnection = (): string => {
  try {
    return localStorage.getItem(CONNECTION_KEY) || ''
  } catch {
    return ''
  }
}
const clearSelection = (): void => {
  try {
    localStorage.removeItem(CLIENT_ID_KEY)
    localStorage.removeItem(CONNECTION_KEY)
    sessionStorage.removeItem(PENDING_KEY)
  } catch {
    /* storage unavailable - ignore */
  }
}
const readPendingLogin = (): boolean => {
  try {
    return sessionStorage.getItem(PENDING_KEY) === '1'
  } catch {
    return false
  }
}
const clearPendingLogin = (): void => {
  try {
    sessionStorage.removeItem(PENDING_KEY)
  } catch {
    /* storage unavailable - ignore */
  }
}

// Role-aware default landing: admins go to the dashboard, everyone else (passport
// employees, or when access cannot be resolved) go to their qualification view.
const RoleAwareLanding = (): JSX.Element => {
  const { access, loading } = useMyAccess()

  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    )
  }

  return <Navigate to={isAdminAccess(access) ? '/admin' : '/qualification'} replace />
}

// Sign-in step: pick a method. Rendered OUTSIDE the Auth0Provider because the choice
// determines which app (client id) the provider must mount with.
const MethodChooser = ({
  onPick,
  onBack,
}: {
  onPick: (clientId: string, connection: string) => void
  onBack: () => void
}): JSX.Element => (
  <Box
    p={4}
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    minHeight="100vh"
  >
    <Stack spacing={2} maxWidth={420} width="100%" alignItems="center">
      <Typography variant="h5" align="center">
        Subcontractor sign-in
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center">
        Choose how you sign in. You will be redirected to KONE&apos;s secure sign-in.
      </Typography>
      {LOGIN_METHODS.map((method, index) => (
        <Button
          key={method.key}
          variant={index === 0 ? 'contained' : 'outlined'}
          fullWidth
          size="large"
          onClick={() => onPick(method.clientId, method.connection)}
        >
          {method.label}
        </Button>
      ))}
      <Button variant="text" onClick={onBack} sx={{ mt: 1 }}>
        &larr; Back to login options
      </Button>
    </Stack>
  </Box>
)

const SubcontractorInner = ({ onNoSession }: { onNoSession: () => void }): JSX.Element => {
  const { isAuthenticated, isLoading, user, loginWithRedirect, logout, getAccessTokenSilently, error: sdkError } =
    useAuth0()
  const location = useLocation()
  const { acquireToken, updateAcquireToken, updateAuthState } = useGlobalStateContext()

  // Detect ?error= returned by Auth0 in the redirect URL so we can surface it.
  const urlParams = new URLSearchParams(location.search)
  const urlError = urlParams.get('error')
  const urlErrorDesc = urlParams.get('error_description')
  const blockedByError = Boolean(urlError) || Boolean(sdkError)

  // Build a clean returnTo: drop the login selector AND any Auth0 error params so we
  // never navigate back to an ?error=... URL (which would keep the error screen up).
  const buildReturnTo = React.useCallback((): string => {
    const params = new URLSearchParams(location.search)
    params.delete('login')
    params.delete('error')
    params.delete('error_description')
    const search = params.toString()
    return `${location.pathname}${search ? `?${search}` : ''}` || '/'
  }, [location])

  const getTokenOrReauth = React.useCallback(async (): Promise<string> => {
    try {
      return await getAccessTokenSilently()
    } catch (err: any) {
      const code = err?.error || err?.code
      if (code === 'consent_required' || code === 'login_required') {
        const connection = readAuthConnection()
        await loginWithRedirect({
          appState: { returnTo: buildReturnTo() },
          authorizationParams: connection ? { connection } : {},
        })
        return ''
      }
      throw err
    }
  }, [getAccessTokenSilently, loginWithRedirect, buildReturnTo])

  // One-shot auto-login: the chooser mounted this provider with the selected app and
  // set the pending flag, so kick off the redirect to Auth0 with the chosen connection.
  const loginTriggeredRef = useRef(false)
  useEffect(() => {
    if (blockedByError || isLoading || isAuthenticated) return
    if (!readPendingLogin()) return
    clearPendingLogin()
    loginTriggeredRef.current = true
    const connection = readAuthConnection()
    loginWithRedirect({
      appState: { returnTo: buildReturnTo() },
      authorizationParams: connection ? { connection } : {},
    })
  }, [blockedByError, isLoading, isAuthenticated, loginWithRedirect, buildReturnTo])

  // No session and no login in progress - the user backed out of the Auth0 screen (or
  // the session expired). Return to the method chooser instead of a dead-end so they
  // can pick a method again.
  useEffect(() => {
    if (blockedByError || isLoading || isAuthenticated) return
    if (readPendingLogin() || loginTriggeredRef.current) return
    clearSelection()
    onNoSession()
  }, [blockedByError, isLoading, isAuthenticated, onNoSession])

  // Feed the Auth0 token + user identity into the shared global state so the existing
  // app components (useGetToken, authState) work unchanged - no MSAL/WithAuthenticator.
  useEffect(() => {
    if (!isAuthenticated) return
    const acquire = async () => ({ accessToken: await getTokenOrReauth() })
    updateAcquireToken(() => acquire as any)
    updateAuthState({ isAuthenticated: true, user: { username: user?.email, name: user?.name } } as any)
  }, [isAuthenticated, getTokenOrReauth, user, updateAcquireToken, updateAuthState])

  // Log the login activity (mirrors KONE). Also exercises the authorizer Auth0 branch.
  const hasLoggedLoginRef = useRef(false)
  useEffect(() => {
    if (!isAuthenticated || hasLoggedLoginRef.current) return
    hasLoggedLoginRef.current = true
    const logAuth0Login = async (): Promise<void> => {
      try {
        const token = await getTokenOrReauth()
        if (!token) return // a re-auth redirect is in progress
        // The shared 'login' dedup flag is set by the KONE flow and not cleared on Auth0
        // sign out, so clear it here to ensure the subcontractor login is always logged.
        localStorage.removeItem('login')
        await logUserAction(token, user?.email || '', CONSTANTS.USER_ACTION.LOGIN)
      } catch (err) {
        console.error('Auth0 login activity logging failed', err)
      }
    }
    logAuth0Login()
  }, [isAuthenticated, getTokenOrReauth, user])

  const retryLogin = () => {
    const connection = readAuthConnection()
    loginWithRedirect({
      appState: { returnTo: buildReturnTo() },
      authorizationParams: connection ? { connection } : {},
    })
  }

  const backToChooser = () => {
    clearAuthPath()
    clearSelection()
    window.location.assign('/')
  }

  const signOut = () => {
    clearAuthPath()
    clearSelection()
    clearMyAccessCache()
    // An unsent batch is keyed by username and would otherwise be offered back
    // at the next sign-in - to whoever that is, and against whatever scope they
    // hold.
    clearAllDrafts()
    logout({ logoutParams: { returnTo: AUTH0_REDIRECT_URI } })
  }

  if (blockedByError) {
    return (
      <Box p={4}>
        <Stack spacing={2} maxWidth={720}>
          <Alert severity="error">
            <strong>Auth0 login failed.</strong>
            <Box component="pre" sx={{ whiteSpace: 'pre-wrap', mt: 1, fontSize: 12 }}>
              {urlError || sdkError?.message}
              {urlErrorDesc ? `\n\n${urlErrorDesc}` : ''}
            </Box>
          </Alert>
          <Stack direction="row" spacing={2}>
            <Button variant="contained" onClick={retryLogin}>
              Try again
            </Button>
            <Button variant="outlined" onClick={backToChooser}>
              Back to login options
            </Button>
          </Stack>
        </Stack>
      </Box>
    )
  }

  // SDK initializing/processing the callback, a login redirect is being kicked off, OR
  // authenticated but the token getter is not yet wired into context. Wait before
  // rendering routes so child components' useGetToken() always finds acquireToken.
  if (
    isLoading ||
    loginTriggeredRef.current ||
    readPendingLogin() ||
    (isAuthenticated && !acquireToken)
  ) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    )
  }

  // No session (backed out / expired) - the no-session effect above is returning to the
  // method chooser; show a spinner during that transition.
  if (!isAuthenticated) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Routes>
      <Route path="/signOut" element={<SignOutForm />} />
      <Route path="/profile/*" element={<ProfilePage onSignOut={signOut} />} />
      <Route path="/qualification" element={<PassportQualificationForm onSignOut={signOut} />} />
      <Route path="/admin" element={<AdminLandingPage onSignOut={signOut} />} />
      <Route path="/settings" element={<Settings onSignOut={signOut} />} />
      <Route path="/terms" element={<TermsAndCondition onSignOut={signOut} />} />
      {/* Land admins on /admin and passport employees on /qualification. */}
      <Route path="/" element={<RoleAwareLanding />} />
    </Routes>
  )
}

const SubcontractorApp = (): JSX.Element => {
  const navigate = useNavigate()
  // The app (client id) chosen on the sign-in step. Restored from storage so the
  // provider re-mounts with the same app across the Auth0 redirect and page reloads.
  const [selectedClientId, setSelectedClientId] = useState<string>(() => readSelectedClientId())

  const onRedirectCallback = (appState?: AppState) => {
    const target = appState?.returnTo || window.location.pathname
    navigate(target, { replace: true })
  }

  const handlePick = (clientId: string, connection: string) => {
    persistSelection(clientId, connection)
    setSelectedClientId(clientId)
  }

  // Return to the method chooser (used when the user backs out of the Auth0 screen).
  const resetToChooser = () => {
    clearSelection()
    setSelectedClientId('')
  }

  const backToChooser = () => {
    clearAuthPath()
    clearSelection()
    window.location.assign('/')
  }

  // When the page is restored from the browser back/forward cache (e.g. the user hits
  // Back on the Auth0 login screen), the frozen JS state can leave a stuck spinner.
  // Force a fresh load so the no-session logic runs and returns to the chooser.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) window.location.reload()
    }
    window.addEventListener('pageshow', onPageShow)
    return () => window.removeEventListener('pageshow', onPageShow)
  }, [])

  // Fail fast with a clear message if Auth0 config is missing, instead of letting
  // the SDK throw a hard-to-diagnose runtime error.
  if (!AUTH0_DOMAIN || (!PASSWORDLESS_CLIENT_ID && !PASSWORD_CLIENT_ID)) {
    return (
      <Box p={4}>
        <Alert severity="error">
          Subcontractor login is not configured. Missing Auth0 environment variables
          (REACT_APP_AUTH0_DOMAIN / REACT_APP_AUTH0_PASSWORDLESS_CLIENT_ID /
          REACT_APP_AUTH0_PASSWORD_CLIENT_ID).
        </Alert>
      </Box>
    )
  }

  // No app chosen yet: show the method chooser (no provider needed for buttons).
  if (!selectedClientId) {
    return <MethodChooser onPick={handlePick} onBack={backToChooser} />
  }

  return (
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={selectedClientId}
      authorizationParams={{
        redirect_uri: AUTH0_REDIRECT_URI,
        audience: AUTH0_AUDIENCE,
      }}
      onRedirectCallback={onRedirectCallback}
      // Persist the session across full page refreshes using rotating refresh
      // tokens. localStorage is required because the in-memory cache is wiped on
      // reload and cookie-based silent auth is blocked by modern browsers.
      useRefreshTokens={true}
      useRefreshTokensFallback={true}
      cacheLocation="localstorage"
    >
      <SubcontractorInner onNoSession={resetToChooser} />
    </Auth0Provider>
  )
}

export default SubcontractorApp
